import { Card } from '../../Card'
import { CardHeader } from '../../CardHeader'
import DesktopIcon from '@/CustomIcons/DesktopIcon'
import { Button } from '@/UIComponents2/Button'
import CloudRecordIcon from '@/CustomIcons/CloudRecordIcon'
import { SimpleSelect } from '@/UIComponents2/Select/SimpleSelect'
import * as Constants from '@/services/Constants'
import ElectronIPC from '@/services/ElectronIPC'
import { useAppContext } from '@/AppContext'
import { ControlButton } from '@/UIComponents2/ControlButton'
import useRootSnackbar from '@/services/useRootSnackbar'
import { useTranslation } from 'react-i18next'
import FolderIcon from '@/CustomIcons/FolderIcon'
import { env } from '@/services/EnvironmentVariables'
import { Alert } from '@/UIComponents2/Alert'
import Tooltip from '@/UIComponents2/Tooltip'
import { Checkbox } from '@/UIComponents2/Checkbox'
import useAppSettingsActions from '@/actions/useAppSettingsActions'
import useAnalytics from '@/services/useAnalytics'
import { AnalyticsEvents } from '@/services/AnalyticsService'
import { chooseFirstScreen } from '@/Logic'
import { useUserContext } from '@/UserContext'
import { logger } from '@/services/Logger'
import { useEffect, useState } from 'react'
import useMediaSourceActions from '@/functionalities/MediaSources/useMediaSourceActions'
import useMediaPermissionActions from '@/functionalities/mediaPermissions/useMediaPermissionActions'
import useRoomActions from '@/services/useRoomActions'
import { useRoomContext } from '@/RoomContext'
import { useCloudRecording } from '@/services/useCloudRecording'

type ScreenRecordingSectionProps = {
    inSession: boolean
}
export function ScreenRecordingSection(props: ScreenRecordingSectionProps) {
    const [appState, appDispatch] = useAppContext()
    const [userState] = useUserContext()
    const [roomState] = useRoomContext()
    const snackbar = useRootSnackbar()
    const { t } = useTranslation()
    const { updateAppSettingItem } = useAppSettingsActions()
    const { logUserActivityWithContext } = useAnalytics()
    const { loadDesktopSources } = useMediaSourceActions()
    const { screenRecordingCommand } = useRoomActions()
    const { isCloudRecording, handleStartCloudRecording, cloudRecordingStarting, hasCloudRecordingPerms } =
        useCloudRecording()

    const inBrowser = !env.IS_ELECTRON

    const [selectedDesktopId, setSelectedDesktopId] = useState<string>('_undef')
    const [previewImageUrl, setPreviewImageUrl] = useState<string>('')
    const [showSelectNewScreen, setSelectNewScreen] = useState<boolean>(false)

    useEffect(() => {
        logger.event({ page: 'session-settings/session-recording', action: 'enter' })

        // This updates `state.mediaSources.desktop`. The mediaSources array may be empty.
        loadDesktopSources()

        return () => {
            logger.event({ page: 'session-settings/session-recording', action: 'exit' })
        }
    }, [])

    useEffect(() => {
        ;(async () => {
            const checkDirectory = async (directory: string) => {
                const r = await ElectronIPC.checkDirectory(directory, { checkWritable: true })
                return r?.success === true
            }

            const isWritable = await checkDirectory(appState.appSettings.screenRecordSaveDirectory)
            if (!isWritable) {
                const path = Constants.defaultDirectories.screen_recording
                const result = await ElectronIPC.checkDirectory(path, { checkWritable: true, createIfNotExists: true })
                if (!result.success) {
                    console.error(`failed to create screenRecordSaveDirectory="${path}".`)
                    appDispatch({
                        type: 'update_app_settings',
                        value: { screenRecordSaveDirectory: '' },
                    })
                } else {
                    appDispatch({
                        type: 'update_app_settings',
                        value: { screenRecordSaveDirectory: path },
                    })
                }
            }
        })()
    }, [appState.appSettings.screenRecordSaveDirectory])

    useEffect(() => {
        if (appState.appSettings.screenRecordDesktop?.id === '_none') {
            setSelectedDesktopId(appState.appSettings.screenRecordDesktop?.id)
            setPreviewImageUrl('')
            return
        }

        if (appState.appSettings.screenRecordDesktop?.id === '_undef' && userState.mediaSources.desktop.length > 0) {
            const screen = chooseFirstScreen(userState.mediaSources.desktop)
            if (screen) {
                appDispatch({
                    type: 'update_app_settings',
                    value: {
                        screenRecordDesktop: {
                            id: screen.id,
                            label: screen.name,
                        },
                    },
                })
            }
        }

        let match =
            userState.mediaSources.desktop.find((d) => d.id === appState.appSettings.screenRecordDesktop?.id) || null
        if (!match) {
            match =
                userState.mediaSources.desktop.find(
                    (d) => d.name === appState.appSettings.screenRecordDesktop?.label
                ) || null
            if (match) {
                appDispatch({
                    type: 'update_app_settings',
                    value: {
                        screenRecordDesktop: {
                            id: match.id,
                            label: match.name,
                        },
                    },
                })
            }
        }

        if (match) {
            setSelectedDesktopId(match.id)
            if (match.thumbnail) {
                setPreviewImageUrl(match.thumbnail.toDataURL())
            }
            setSelectNewScreen(false)
        } else {
            // failed to find the selected desktop in `appSettings.screenRecordDesktop`; show message.
            setSelectedDesktopId('_undef')
            setPreviewImageUrl('')
            if (userState.mediaSources.desktop.length > 0) {
                setSelectNewScreen(true)
            }
        }
    }, [userState.mediaSources.desktop, appState.appSettings.screenRecordDesktop])

    function cloudRecordingDisabledReason(): string | null {
        if (!hasCloudRecordingPerms) {
            return 'Upgrade your plan to use cloud recording'
        }
        if (!props.inSession) {
            return 'You must join the session before you can start recording'
        }
        if (isCloudRecording) {
            return 'This session is being recorded'
        }
        if (cloudRecordingStarting) {
            return 'Cloud recording is starting'
        }
        return null
    }

    function localRecordingDiabledReason(): string | null {
        if (inBrowser) {
            return 'Please download the Muse app to use local screen recording'
        }
        if (!props.inSession) {
            return 'You must join the session before you can start recording'
        }
        return null
    }

    function onlyRecordAudioToggled(from: boolean, to: boolean) {
        if (from === true && to === false) {
            // select first screen
            const screen = chooseFirstScreen(userState.mediaSources.desktop)
            if (screen) {
                appDispatch({
                    type: 'update_app_settings',
                    value: {
                        screenRecordDesktop: {
                            id: screen.id,
                            label: screen.name,
                        },
                    },
                })
            }
        } else if (from === false && to === true) {
            // set desktop selection to '_none'
            appDispatch({
                type: 'update_app_settings',
                value: {
                    screenRecordDesktop: { id: '_none', label: '' },
                },
            })
        }
    }

    const renderCloudRecordingButton = () => {
        return (
            <Tooltip text={cloudRecordingDisabledReason()}>
                <Button
                    variant="secondary"
                    className={isCloudRecording ? stopButtonClasses : startButtonClasses}
                    disabled={cloudRecordingDisabledReason() !== null}
                    onClick={() => {
                        if (!isCloudRecording && !cloudRecordingStarting) {
                            handleStartCloudRecording()
                        }
                    }}
                >
                    <CloudRecordIcon />{' '}
                    {cloudRecordingStarting ? 'Starting' : isCloudRecording ? 'Recording' : 'Start Cloud'}
                </Button>
            </Tooltip>
        )
    }

    function desktopToValue(selectedDesktopId: string): string {
        if (selectedDesktopId === '_none' || selectedDesktopId === '_undef') {
            return selectedDesktopId
        }

        const match = userState.mediaSources.desktop.filter((d) => d.id === selectedDesktopId)[0] || null
        if (match) {
            return match.id
        } else {
            return '_undef'
        }
    }

    function getDesktopOptions() {
        let items = [
            { value: '_undef', label: t('Select Screen') },
            { value: '_none', label: t('None') },
        ]
        for (const desktop of userState.mediaSources.desktop) {
            items.push({
                value: desktop.id,
                label: desktop.name,
            })
        }
        return items
    }

    const desktopOptions = getDesktopOptions()
    const selectedDesktopName = desktopOptions.find((d) => d.value === selectedDesktopId)?.label ?? 'None'
    const isLocalRecording = roomState.isScreenRecording

    const startButtonClasses = 'w-full text-muse-yellow shadow-glow bg-white/3 flex gap-3 pl-2'
    const stopButtonClasses = 'w-full text-muse-danger shadow-glow bg-white/3 flex gap-3 pl-2'

    return (
        <Card className="md:p-[10px] flex flex-col gap-[10px]">
            <div className="grid grid-cols[1fr] md:grid-cols-[50%_1fr] gap-[33px]">
                <div className="flex flex-col pl-[20px] py-[15px] gap-7">
                    <div className="flex justify-between">
                        <CardHeader icon={DesktopIcon} title="Screen Recording" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <Tooltip text={localRecordingDiabledReason()}>
                            <Button
                                variant="secondary"
                                className={isLocalRecording ? stopButtonClasses : startButtonClasses}
                                disabled={inBrowser || !props.inSession}
                                onClick={() => {
                                    if (isLocalRecording) {
                                        screenRecordingCommand('stop')
                                    } else {
                                        screenRecordingCommand('start')
                                    }

                                    const event = isLocalRecording
                                        ? AnalyticsEvents.CLICKED_STOP_SCREEN_RECORDING_BUTTON
                                        : AnalyticsEvents.CLICKED_START_SCREEN_RECORDING_BUTTON

                                    logUserActivityWithContext(event, { componentSource: 'SessionRecordingSettings' })
                                }}
                            >
                                <DesktopIcon /> {isLocalRecording ? 'Stop' : 'Start'} Local
                            </Button>
                        </Tooltip>
                        {renderCloudRecordingButton()}
                    </div>
                    <div className="flex flex-col gap-4">
                        {env.OS === 'win32' && (
                            <Checkbox
                                checked={appState.appSettings.showWindowsDuringScreenshare}
                                label="Show Muse in screen recording"
                                onChange={(val) => {
                                    if (isLocalRecording) {
                                        snackbar.show({
                                            severity: 'error',
                                            message: t('Cannot change settings while screen recording is in progress.'),
                                        })
                                        return
                                    }

                                    updateAppSettingItem('showWindowsDuringScreenshare', val)
                                    ElectronIPC.setWindowOptions({ isContentProtectionEnabled: !val })
                                    logUserActivityWithContext(AnalyticsEvents.TOGGLED_SHOW_MUSE_IN_SCREEN_RECORDING, {
                                        showWindowsDuringScreenshare: val,
                                    })
                                }}
                            />
                        )}
                        <Checkbox
                            checked={appState.appSettings.screenRecordOnlyAudio}
                            label="Only record session audio"
                            onChange={(val) => {
                                if (isLocalRecording) {
                                    snackbar.show({
                                        severity: 'error',
                                        message: t('Cannot change settings while screen recording is in progress.'),
                                    })
                                    return
                                }

                                updateAppSettingItem('screenRecordOnlyAudio', val)
                                onlyRecordAudioToggled(appState.appSettings.screenRecordOnlyAudio, val)
                                logUserActivityWithContext(AnalyticsEvents.TOGGLED_ONLY_RECORD_SESSION_AUDIO, {
                                    screenRecordOnlyAudio: val,
                                })
                            }}
                        />
                    </div>
                    <div>
                        <SimpleSelect
                            label="Select Screen"
                            disabled={inBrowser}
                            items={desktopOptions}
                            value={desktopToValue(selectedDesktopId)}
                            onOpenChange={(open) => open && loadDesktopSources()}
                            onChange={(val) => {
                                if (isLocalRecording) {
                                    snackbar.show({
                                        severity: 'error',
                                        message: 'Cannot change settings while screen recording is in progress.',
                                    })
                                } else {
                                    const display = userState.mediaSources.desktop.find((a) => a.id === val)
                                    appDispatch({
                                        type: 'update_app_settings',
                                        value: {
                                            screenRecordDesktop: {
                                                id: val,
                                                label: display?.name || '',
                                            },
                                        },
                                    })

                                    if (appState.appSettings.screenRecordOnlyAudio) {
                                        appDispatch({
                                            type: 'update_app_settings',
                                            value: { screenRecordOnlyAudio: false },
                                        })
                                    }
                                    if (val === '_none') {
                                        appDispatch({
                                            type: 'update_app_settings',
                                            value: { screenRecordOnlyAudio: true },
                                        })
                                    }

                                    logUserActivityWithContext(AnalyticsEvents.CHANGED_SCREEN_TO_RECORD, {
                                        screenRecordDesktop: val,
                                    })
                                }
                            }}
                            widthMatching
                        />
                        {showSelectNewScreen && (
                            <div className="pt-2">
                                <Alert variant="outlined" level="danger">
                                    {t('Please select a screen to record.')}
                                </Alert>
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col gap-2">
                        <div>Save Location</div>
                        <div className="grid grid-cols-[1fr_min-content] gap-2">
                            <Tooltip text={appState.appSettings.screenRecordSaveDirectory}>
                                <Button
                                    variant="rectangle"
                                    className="border-muse-toplit truncate"
                                    disabled={inBrowser}
                                    onClick={async () => {
                                        if (isLocalRecording) {
                                            snackbar.show({
                                                severity: 'error',
                                                message: t(
                                                    'Cannot change settings while screen recording is in progress.'
                                                ),
                                            })
                                        } else {
                                            const r = await ElectronIPC.openFileDialog({
                                                defaultPath: appState.appSettings.screenRecordSaveDirectory,
                                                properties: ['openDirectory'],
                                            })
                                            const selectedDirectoryPath = (r?.paths || [])[0] || null
                                            if (selectedDirectoryPath) {
                                                appDispatch({
                                                    type: 'update_app_settings',
                                                    value: { screenRecordSaveDirectory: selectedDirectoryPath },
                                                })
                                            }
                                        }
                                    }}
                                >
                                    <span className="truncate">
                                        {appState.appSettings.screenRecordSaveDirectory || '(none)'}
                                    </span>
                                </Button>
                            </Tooltip>
                            <Tooltip text="Browse recording folder">
                                <ControlButton
                                    variant="secondary"
                                    disabled={inBrowser || !appState.appSettings.screenRecordSaveDirectory}
                                    className="shadow-glow"
                                    onClick={() => {
                                        ElectronIPC.openPath(appState.appSettings.screenRecordSaveDirectory)
                                    }}
                                >
                                    <FolderIcon style={{ fontSize: '1.3rem' }} />
                                </ControlButton>
                            </Tooltip>
                        </div>
                    </div>
                </div>
                <ScreenPreview
                    previewImageUrl={previewImageUrl}
                    screenName={selectedDesktopName}
                    selectedDesktopId={selectedDesktopId}
                />
            </div>
            <Alerts
                inSession={props.inSession}
                inBrowser={inBrowser}
                hasPermission={userState.mediaPermissions.desktop ?? false}
            />
        </Card>
    )
}

function ScreenPreview(props: { previewImageUrl: string; screenName: string; selectedDesktopId: string }) {
    if (props.selectedDesktopId == '_undef' || props.selectedDesktopId == '_none' || !props.selectedDesktopId) {
        return (
            <div className="flex flex-col gap-2">
                <div className="w-full aspect-1/1 bg-neutral-700 rounded-[28px]"></div>
                <div className="text-center t-lg-medium text-muse-fg/70">No screen selected</div>
            </div>
        )
    }

    if (!props.previewImageUrl) {
        return (
            <div className="flex flex-col gap-2">
                <div className="w-full aspect-1/1 bg-neutral-700 rounded-[32px]"></div>
                <div className="text-center t-lg-medium text-muse-fg/70">Failed to load preview</div>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-2">
            <img className="w-full aspect-1/1 object-cover rounded-[32px]" src={props.previewImageUrl} />
            <div className="text-center t-lg-medium text-muse-fg/70">{props.screenName}</div>
        </div>
    )
}

function Alerts(props: { inSession: boolean; inBrowser: boolean; hasPermission: boolean }) {
    const { t } = useTranslation()
    const { openPrivacyPreferencesSettings } = useMediaPermissionActions()

    if (!props.inBrowser && !props.hasPermission) {
        return (
            <Alert variant="outlined" level="danger">
                <div>
                    {t('Screen recording permission not granted.')}
                    {' ' + t('You need to restart the app for permission changes to take effect.')}
                </div>
                <Button variant="link" onClick={() => openPrivacyPreferencesSettings('screen')}>
                    {t('Open system preferences')}
                </Button>
            </Alert>
        )
    }

    if (!props.inSession) {
        return (
            <Alert variant="outlined" level="warning">
                You must join the session before you can start recording
            </Alert>
        )
    }
    return null
}
